import React, { forwardRef, useCallback, useMemo } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/** The wire/state format. Every call site keeps holding a plain string in this
 *  shape, exactly as it did with `<input type="date">`, so no caller has to
 *  learn dayjs and no payload builder or validator needs changing. */
const ISO_FORMAT = 'YYYY-MM-DD';

/** What the user reads. Mirrors `formatDateHandler` in `utils/common` ('MMM dd,
 *  yyyy' in date-fns tokens), so a date typed into a field and the same date
 *  rendered back in a table or detail page look identical. */
const DISPLAY_FORMAT = 'MMM DD, YYYY';

/** Both formats are accepted when typing: the first is what gets displayed,
 *  the rest are extra parse candidates (rc-picker behaviour for array formats).
 *  Keeping ISO parseable means a pasted `2026-09-08` still works. */
const ACCEPTED_FORMATS = [DISPLAY_FORMAT, ISO_FORMAT];

/**
 * Parse a caller-supplied value into a dayjs instance, or null.
 *
 * Strictly `YYYY-MM-DD` — the only shape this component emits, and the only
 * shape the API hands back: every field bound to a `DateField` maps to a Django
 * `models.DateField` (`po_date`, `expected_delivery_date`, `invoice_date`,
 * `transfer_date`, `issuance_date`, `conducted_date`, `received_date`), which
 * DRF serialises without a time component. No caller passes a datetime.
 *
 * Anything else returns null — deliberately. A lenient `dayjs(value)` fallback
 * used to sit here and was actively harmful on dayjs 1.11.20:
 *   - `'2026-09-08T00:00:00Z'` renders as 2026-09-07 in America/Los_Angeles,
 *     and `'2026-09-08T00:00:00+04:00'` as 2026-09-07 in UTC — a timestamp is
 *     resolved in the *browser's* zone, so the date shown depends on where the
 *     user is sitting.
 *   - Malformed input is silently coerced rather than rejected: `'2026-13-45'`
 *     becomes 2027-02-14 and `0` becomes 1970-01-01.
 * In every one of those cases the field showed a plausible *wrong* date, which
 * is worse than showing nothing. A date field should not guess.
 */
const toDayjs = (value) => {
  if (value === undefined || value === null || value === '') return null;
  // A dayjs instance is already an exact date — no parsing, so no guessing.
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  const parsed = dayjs(value, ISO_FORMAT, true);
  return parsed.isValid() ? parsed : null;
};

/**
 * Themed replacement for `<input type="date" className="form-control">`.
 *
 * The native control's calendar is drawn by the operating system, which is why
 * its selection highlight stayed blue no matter what CSS the app shipped. antd's
 * DatePicker is real DOM and so inherits `colorPrimary: #D3001A` from
 * `theme/antdTheme.js` — selected day, "today" ring and range fill all render in
 * the brand red with no extra styling.
 *
 * The contract is deliberately identical to the native input's:
 *   - `value`  — a `YYYY-MM-DD` string (or '' / null / undefined for empty)
 *   - `onChange(nextValue)` — called with a `YYYY-MM-DD` string, or '' when
 *     cleared. NOT an event: call sites drop the `e.target.value` unwrapping.
 *   - `min` / `max` — `YYYY-MM-DD` strings, same meaning as the HTML attributes.
 *
 * Popup stacking is left to antd on purpose (no `getPopupContainer`): the panel
 * portals to `document.body`, so a modal body with `overflow-y: auto` cannot
 * clip it, and antd's zIndex context puts it above whichever modal opened it.
 *
 * Ref-forwarding matters for react-hook-form. Call sites spread `{...field}`
 * from a `Controller`, which includes `field.ref`; on the native input that ref
 * landed on the DOM node, so `shouldFocusError` could focus the first invalid
 * field (e.g. a missing `po_date`) on a failed submit. A plain function
 * component swallows the ref — React warns, and the focus silently stops
 * working. Forwarding to antd's `DatePicker` restores it: its ref is an
 * rc-picker `PickerRef` (`{ nativeElement, focus, blur }`), and RHF's
 * `useController` stores the instance and calls `.focus()` on it, which
 * rc-picker routes to the picker's own `<input>`.
 */
const DateField = forwardRef(({
  value,
  onChange,
  min,
  max,
  id,
  name,
  placeholder = 'Select date',
  disabled = false,
  allowClear = true,
  status,
  className = '',
  style,
  onBlur,
  ...rest
}, ref) => {
  const selected = useMemo(() => toDayjs(value), [value]);
  const minDate = useMemo(() => toDayjs(min), [min]);
  const maxDate = useMemo(() => toDayjs(max), [max]);

  const handleChange = useCallback(
    (date) => {
      if (!onChange) return;
      onChange(date && date.isValid() ? date.format(ISO_FORMAT) : '');
    },
    [onChange],
  );

  return (
    <DatePicker
      ref={ref}
      id={id}
      name={name}
      value={selected}
      onChange={handleChange}
      onBlur={onBlur}
      minDate={minDate || undefined}
      maxDate={maxDate || undefined}
      format={ACCEPTED_FORMATS}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      status={status}
      // Matches the 40px `controlHeight` the module's inputs and FilterSelects
      // use, and fills its bootstrap column the way `.form-control` did.
      className={className}
      style={{ width: '100%', ...style }}
      {...rest}
    />
  );
});

DateField.displayName = 'DateField';

export { ISO_FORMAT, DISPLAY_FORMAT };
export default DateField;
